/**
 * Reusable preview card — shown after a file is parsed (or as a manual
 * entry pad when the parser can't read the file).
 *
 * Three modes:
 *   - rent_roll: shows unit / occupancy summary; lets operator edit values
 *   - vehicles:  shows total vehicle count
 *   - myvip:     shows myvip-profile unit count
 *
 * Props:
 *   summary         — { total_units, occupied_units, ... } depending on kind
 *   kind            — 'rent_roll' | 'vehicles' | 'myvip'
 *   onEdit          — (newSummary) => void   (rent_roll only)
 *   onConfirm       — () => void
 *   onReupload      — () => void
 *   startInEdit     — boolean; auto-open the editor (used when parse failed
 *                     or user picked manual entry)
 *   confirmDisabled — boolean; renders Confirm as disabled
 */
import React, { useState, useEffect } from 'react';

const BRAND = '#3F66A0';

export default function ParsedPreviewCard({
  summary, kind, onEdit, onConfirm, onReupload, startInEdit = false, confirmDisabled = false,
}) {
  const [editing, setEditing] = useState(startInEdit);
  const [draft, setDraft] = useState(summary || {});

  // If parent flips startInEdit (e.g. after a parse fail), reflect it.
  useEffect(() => {
    if (startInEdit) {
      setEditing(true);
      setDraft(summary || {});
    }
  }, [startInEdit, summary]);

  if (!summary) return null;

  const renderRentRoll = () => {
    const s = editing ? draft : summary;
    const fields = [
      ['total_units', 'Total Units'],
      ['occupied_units', 'Occupied'],
      ['vacant_units', 'Vacant'],
      ['model_units', 'Model'],
      ['ntv_units', 'NTV'],
      ['future_residents', 'Future Residents'],
    ];
    const headlineHasData = (s.total_units || 0) > 0;
    return (
      <>
        {headlineHasData ? (
          <p className="audit-preview-headline">
            Detected <strong>{s.total_units}</strong> units, <strong>{s.occupied_units}</strong> occupied
            {s.vacant_units > 0 && <>, <strong>{s.vacant_units}</strong> vacant</>}
            {s.model_units > 0 && <>, <strong>{s.model_units}</strong> model</>}
            {s.ntv_units > 0 && <>, <strong>{s.ntv_units}</strong> NTV</>}.
          </p>
        ) : (
          <p className="audit-preview-headline" style={{ color: '#64748b' }}>
            No values detected — fill in the rent roll counts below, then confirm.
          </p>
        )}
        <div className="audit-preview-grid">
          {fields.map(([k, label]) => (
            <div key={k} className="audit-preview-cell">
              <span>{label}</span>
              {editing ? (
                <input
                  type="number"
                  min="0"
                  value={draft[k] ?? 0}
                  onChange={(e) => setDraft({ ...draft, [k]: parseInt(e.target.value || 0) })}
                />
              ) : (
                <strong>{summary[k] ?? 0}</strong>
              )}
            </div>
          ))}
        </div>
      </>
    );
  };

  const renderVehicles = () => (
    <p className="audit-preview-headline">
      Detected <strong>{summary.total_vehicles ?? 0}</strong> vehicles
      {summary.unique_units_with_vehicles ? <> across <strong>{summary.unique_units_with_vehicles}</strong> units</> : null}.
    </p>
  );

  const renderMyVIP = () => (
    <p className="audit-preview-headline">
      Detected <strong>{summary.myvip_profile_units ?? 0}</strong> units with active MyVIP profiles.
    </p>
  );

  return (
    <div className="audit-preview-card">
      <h3>Parser Preview</h3>
      {kind === 'rent_roll' && renderRentRoll()}
      {kind === 'vehicles' && renderVehicles()}
      {kind === 'myvip' && renderMyVIP()}

      <div className="audit-preview-actions">
        {kind === 'rent_roll' && onEdit && (
          editing ? (
            <>
              <button
                type="button"
                className="audit-btn"
                onClick={() => { onEdit(draft); setEditing(false); }}
              >Save edits</button>
              <button
                type="button"
                className="audit-btn ghost"
                onClick={() => { setDraft(summary); setEditing(false); }}
              >Cancel</button>
            </>
          ) : (
            <button type="button" className="audit-btn ghost" onClick={() => setEditing(true)}>
              Edit values
            </button>
          )
        )}
        {onReupload && (
          <button type="button" className="audit-btn ghost" onClick={onReupload}>Re-upload</button>
        )}
        <button
          type="button"
          className="audit-btn primary"
          onClick={onConfirm}
          disabled={confirmDisabled || editing}
          style={{
            opacity: (confirmDisabled || editing) ? 0.5 : 1,
            cursor: (confirmDisabled || editing) ? 'not-allowed' : 'pointer',
          }}
          title={editing ? 'Save edits before confirming' : ''}
        >
          Confirm →
        </button>
      </div>
    </div>
  );
}
